/**
 * Web app store — UI state for the editor.
 * Mirrors Tauri appStore patterns for layout parity.
 */
import { create } from 'zustand';

export type ActivePanel = 'canvas' | 'events' | 'entities' | 'assets' | 'settings' | 'event-workspace' | 'insights';

export type FlyerProjectDoc = {
  _id: string;
  id: string;
  name: string;
  event_id?: string;
  canvas_state: unknown;
  attached_assets: string[];
  attached_entities: Array<{ entity_type: string; entity_id: string }>;
  created_at: number;
  updated_at: number;
};

interface AppState {
  activePanel: ActivePanel;
  setActivePanel: (panel: ActivePanel) => void;
  showGenerationModal: boolean;
  setShowGenerationModal: (show: boolean) => void;
  currentProjectId: string | null;
  setCurrentProjectId: (id: string | null) => void;
  /** Selected project (series) id for project-scoped chat and context */
  currentSeriesId: string | null;
  setCurrentSeriesId: (id: string | null) => void;
  currentAccountId: string | null;
  setCurrentAccountId: (id: string | null) => void;
  /** When in org context, optional team filter for projects/flyers */
  currentTeamId: string | null;
  setCurrentTeamId: (id: string | null) => void;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
  showWelcomeModal: boolean;
  setShowWelcomeModal: (show: boolean) => void;
  /** User ID for "User activity" modal (avatar/member/search entry) */
  activityUserId: string | null;
  setActivityUserId: (id: string | null) => void;
  /** Event entity ID for EventWorkspace view */
  currentEventWorkspaceId: string | null;
  setCurrentEventWorkspaceId: (id: string | null) => void;
  /** Navigate to EventWorkspace for a given event entity ID. */
  openEventWorkspace: (eventId: string) => void;
}

export const useAppStore = create<AppState>((set) => ({
  activePanel: 'canvas',
  setActivePanel: (panel) => set({ activePanel: panel }),
  showGenerationModal: false,
  setShowGenerationModal: (show) => set({ showGenerationModal: show }),
  currentProjectId: null,
  setCurrentProjectId: (id) => set({ currentProjectId: id }),
  currentSeriesId: null,
  setCurrentSeriesId: (id) => set({ currentSeriesId: id }),
  currentAccountId: null,
  setCurrentAccountId: (id) => set({ currentAccountId: id }),
  currentTeamId: null,
  setCurrentTeamId: (id) => set({ currentTeamId: id }),
  showSearch: false,
  setShowSearch: (show) => set({ showSearch: show }),
  showWelcomeModal: false,
  setShowWelcomeModal: (show) => set({ showWelcomeModal: show }),
  activityUserId: null,
  setActivityUserId: (id) => set({ activityUserId: id }),
  currentEventWorkspaceId: null,
  setCurrentEventWorkspaceId: (id) => set({ currentEventWorkspaceId: id }),
  openEventWorkspace: (eventId) =>
    set({ activePanel: 'event-workspace', currentEventWorkspaceId: eventId }),
}));
